/**
 * Filtro por proprietário — fonte de verdade do sentinela "sem proprietário".
 *
 * O chip de proprietário emite `user_id`s e, opcionalmente, o sentinela
 * `__no_owner__`. Os dois podem coexistir na mesma seleção ("negócios da Luiza
 * **e** os sem dono"), e é justamente a mistura que estava sendo perdida: quatro
 * call sites usavam `if (inclui sentinela) → is(null); else if …`, o `else if`
 * descartando silenciosamente os ids concretos. Em `fetchAllFilteredDealIds` isso
 * fazia "Selecionar todos" marcar só os negócios sem dono.
 *
 * A tradução da mistura para PostgREST é `or=(owner_id.is.null,owner_id.in.(…))`
 * — mesma forma já usada em `dealsSavedFilterQuery`. Do lado do SQL, o
 * `_filter_rule_predicate` e o `p_include_no_owner` das RPCs de listagem já
 * entendem o sentinela; este módulo cobre só o caminho PostgREST.
 */

/** Valor emitido pelo chip de proprietário para "sem proprietário". */
export const NO_OWNER_SENTINEL = "__no_owner__";

export interface OwnerSelection {
  /** A seleção inclui registros sem proprietário (`owner_id IS NULL`). */
  includeNoOwner: boolean;
  /** `user_id`s concretos selecionados, sem o sentinela e sem duplicatas. */
  ownerIds: string[];
}

/**
 * Separa o sentinela dos `user_id`s concretos. Aceita o formato solto que as
 * páginas usam (`string`, `string[]`, `'all'`, `null`) e normaliza para um par.
 */
export function splitOwnerSelection(
  owners: string | readonly string[] | null | undefined,
): OwnerSelection {
  if (!owners || owners === "all") return { includeNoOwner: false, ownerIds: [] };

  const list = Array.isArray(owners) ? owners : [owners];
  const ownerIds: string[] = [];
  let includeNoOwner = false;

  for (const raw of list) {
    if (typeof raw !== "string") continue;
    const value = raw.trim();
    if (!value || value === "all") continue;
    if (value === NO_OWNER_SENTINEL) {
      includeNoOwner = true;
    } else if (!ownerIds.includes(value)) {
      ownerIds.push(value);
    }
  }

  return { includeNoOwner, ownerIds };
}

/**
 * Quoting idêntico ao de `PostgrestFilterBuilder.in()`: só aspeia o valor quando
 * ele contém `,` `(` ou `)`. Mantém o `or=(…)` byte-compatível com o `in.(…)` que
 * o supabase-js geraria, para os dois caminhos devolverem o mesmo conjunto.
 */
function quoteForInList(value: string): string {
  return /[,()]/.test(value) ? `"${value}"` : value;
}

interface OwnerQueryBuilder<Q> {
  is(column: string, value: null): Q;
  eq(column: string, value: string): Q;
  in(column: string, values: readonly string[]): Q;
  or(filters: string): Q;
}

/**
 * Aplica a seleção de proprietário a um query builder do PostgREST.
 *
 * - seleção vazia → não toca a query
 * - só o sentinela → `is(field, null)`
 * - só ids → `eq` (1) ou `in` (N)
 * - **mistura** → `or(field.is.null, field.in.(ids))`
 *
 * Combina com outros filtros já aplicados como AND, então empilhar isto depois de
 * um `recordOwnerFilter` (visibilidade de registro) preserva a restrição — e a
 * interseção "só meus registros" + "sem proprietário" ser vazia é o resultado
 * correto: quem só vê os próprios registros nunca vê um registro sem dono.
 */
export function applyOwnerFilter<Q extends OwnerQueryBuilder<Q>>(
  q: Q,
  field: string,
  owners: string | readonly string[] | null | undefined,
): Q {
  const { includeNoOwner, ownerIds } = splitOwnerSelection(owners);

  if (!includeNoOwner && ownerIds.length === 0) return q;
  if (!includeNoOwner) {
    return ownerIds.length === 1 ? q.eq(field, ownerIds[0]) : q.in(field, ownerIds);
  }
  if (ownerIds.length === 0) return q.is(field, null);

  const inList = ownerIds.map(quoteForInList).join(",");
  return q.or(`${field}.is.null,${field}.in.(${inList})`);
}
